import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import AccommodationSameRoomOptionTileHeaderArea from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileHeaderArea'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import BedbankCancellationPolicy from '../BedbankCancellationPolicy'
import BedbankPromotionDetails from '../BedbankPromotionDetails'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import WalledContent from 'components/Common/WalledContent'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import BedbankPackageTilePricing from '../BedbankPackageTilePricing'
import moment from 'moment'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationOfferInclusionsExpandable from 'components/OfferPage/Accommodation/AccommodationOfferInclusionsExpandable'
import { selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import TextButton from 'LuxKit/Button/TextButton'
import useRoomOptionRateBedbankQuote from 'hooks/Insurance/useRoomOptionRateBedbankQuote'
import BedbankRoomOptionRateInsurance from '../BedbankRoomOptionRateInsurance'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'
import { rem } from 'polished'
import { useBedbankSingleRoomAddToUserCartCallback, useBedbankSingleRoomBookNowCallback } from 'hooks/OfferPage/useBedbankBuyCallbacks'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import BedbankPackageTileCTAs from '../BedbankPackageTileCTAs'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import useBedbankRateStatusCreditsCalculation from 'loyalty/hooks/useBedbankRateStatusCreditsCalculation'
import useHotelTierProgressionEligibility from 'loyalty/hooks/useHotelTierProgressionEligibility'
import RateTierProgressionCallOutBanner from 'loyalty/components/Banners/RateTierProgressionCallOutBanner'

interface Props {
  packageNumber: number
  rate: App.BedbankRate
  packageView: App.BedbankOfferPackageView
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  rooms: Array<OfferPageRoom>
  flightOrigin?: App.Airport
  isLoggedIn: boolean
  isFlexCfmrBedbankPackagesEnabled: boolean
  isSpoofed?: boolean
  subscriber?: App.OrderSubscriber
  connectRooms: boolean
  /**
   * Unique ID for the list of rates this rate was shown under
   * Required for analytics
   */
  rateListId: string
  /**
   * Position this room was in the list of rooms on the offer page
   * Required for analytics
   */
  roomPosition: number
  /**
   * Unique ID for the list of rooms on the offer page
   * Required for analytics
   */
  roomListId: string
}

function BedbankMultiSameRoomRoomOption(props: Props) {
  const {
    rate,
    packageNumber,
    packageView,
    checkInDate,
    checkOutDate,
    offer,
    rooms,
    flightOrigin,
    rateListId,
    roomListId,
    roomPosition,
    isFlexCfmrBedbankPackagesEnabled,
    isSpoofed,
    subscriber,
    connectRooms,
    isLoggedIn,
  } = props

  const intl = useIntl()

  const promotion = packageView.promotions[rate?.promotionId ?? '']

  const { bundleRates, payAtPropertyRates } = packageView
  const bundleRate = useMemo(() => bundleRates.find((bundleRate) => bundleRate.id === rate.id) ?? rate, [bundleRates, rate])

  const departureTooEarly = useMemo(() => {
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInDate])

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  const showModal = useContext(ModalContext)

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)

  const nights = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 0
    return moment(checkOutDate).diff(moment(checkInDate), 'days')
  }, [checkInDate, checkOutDate])

  const { statusCredits: rateStatusCredits, isLoading: statusCreditsLoading } = useBedbankRateStatusCreditsCalculation({
    offer,
    rate,
    nights,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount,
  })

  const {
    showTierProgressionBanner,
    highestUnlockableTier,
    highestUnlockableTierName,
  } = useHotelTierProgressionEligibility({
    statusCredits: rateStatusCredits,
    nights,
    checkoutDate: checkOutDate,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount || statusCreditsLoading,
  })

  const onClickLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const { quote, isFetching: isQuoteFetching, error: quoteError, onSelectInsurance, isInsuranceSelected } = useRoomOptionRateBedbankQuote({
    country: offer.property.address.countryName,
    coverAmount: rate.totals.total,
    checkIn: checkInDate,
    checkout: checkOutDate,
    travellers: rooms,
    useCache: true,
    disabled: !isFlexCfmrBedbankPackagesEnabled,
  })

  const isRefundableRate = rate.refundable || rate.partiallyRefundable

  const onBook = useBedbankSingleRoomBookNowCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    originAirport: flightOrigin,
    isLoggedIn,
    isSpoofed,
    subscriber,
    rateListId,
    roomPosition,
    roomListId,
    flightPrice,
    connectRooms,
  })

  const onAddToUserCart = useBedbankSingleRoomAddToUserCartCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    originAirport: flightOrigin,
    isLoggedIn,
    isSpoofed,
    subscriber,
    rateListId,
    roomPosition,
    roomListId,
    flightPrice,
    connectRooms,
  })

  return <AccommodationSameRoomOptionTile>
    {showTierProgressionBanner && highestUnlockableTier && (
      <AccommodationSameRoomOptionTileHeaderArea>
        <RateTierProgressionCallOutBanner
          highestUnlockableTier={highestUnlockableTier}
          highestUnlockableTierName={highestUnlockableTierName}
          checkoutDate={checkOutDate}
        />
      </AccommodationSameRoomOptionTileHeaderArea>
    )}
    <ScrollAnchor id={`mobile-${rate.id}`} vOffset={rem(350)} />
    <AccommodationSameRoomOptionTileDetails
      packageNumber={packageNumber}
      optionName={promotion?.name}
      tagLineLabel={<>
        {rate.corporate && <ProductTypeLabel productType="corporate" />}
        {promotion && <ProductTypeLabel productType="limited_time_special" />}
      </>}
    >
      {promotion && <BedbankPromotionDetails
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        promotion={promotion}
        rate={rate}
      />}
      {!promotion && <BedbankCancellationPolicy
        rate={rate}
        showNonCancellation={!isFlexCfmrBedbankPackagesEnabled}
      />}
      <AccommodationOfferInclusionsExpandable
        content={promotion?.rateInclusionsLong}
        inclusions={promotion?.inclusions ?? rate.inclusions}
        maxHeight={80}
        collapsedHeight={70}
      />
      {!isRefundableRate && isFlexCfmrBedbankPackagesEnabled && <div>
        <BedbankRoomOptionRateInsurance
          isLoading={isQuoteFetching}
          onChange={onSelectInsurance}
          errorOnQuote={!!quoteError}
          quote={quote}
          refundTitle={intl.formatMessage({
            id: 'bTCuzcNWKDe-utOavIGE',
            defaultMessage: 'Full refund for unforeseen events',
            description: 'Insurance refund title for bedbank room option rate',
          })}
          refundDetails={intl.formatMessage({
            id: 'JLlAlfazJ_IIv-Scu750',
            defaultMessage: '(Hotel only)',
            description: 'Insurance refund detail indicating hotel only coverage for bedbank room option rate',
          })}
        />
      </div>}
    </AccommodationSameRoomOptionTileDetails>

    <AccommodationSameRoomOptionTilePricing>
      <WalledContent
        enforced={offer.walledGarden}
        hidePricing={offer.hidePricing}
      >
        <BedbankPackageTilePricing
          promotion={promotion}
          rate={rate}
          bundleRate={bundleRate}
          payAtPropertyRates={payAtPropertyRates}
          flightPrice={flightPrice}
          departureTooEarly={departureTooEarly}
          ratePosition={packageNumber}
          showPoints
          rateListId={rateListId}
          roomPosition={roomPosition}
          roomListId={roomListId}
          onBookNow={onBook}
          onAddToUserCartNow={onAddToUserCart}
          isInsuranceSelected={isInsuranceSelected}
        />
      </WalledContent>
    </AccommodationSameRoomOptionTilePricing>

    <AccommodationSameRoomOptionTileCTA>
      <WalledContent
        enforced={offer.walledGarden}
        hidePricing={offer.hidePricing}
        wall={<TextButton
          kind="primary"
          size="large"
          onClick={onClickLogin}
          fit="full-width"
        >
          {intl.formatMessage({
            id: 'JmuxAlPdzI8lHlYr-sJT',
            defaultMessage: 'Log in to view prices',
            description: 'CTA button prompting user to log in to see bedbank room prices',
          })}
        </TextButton>}
      >
        <BedbankPackageTileCTAs
          rate={rate}
          payAtPropertyRates={payAtPropertyRates}
          flightPrice={flightPrice}
          departureTooEarly={departureTooEarly}
          ratePosition={packageNumber}
          onBookNow={onBook}
          onAddToUserCartNow={onAddToUserCart}
          isInsuranceSelected={isInsuranceSelected}
        />
      </WalledContent>
    </AccommodationSameRoomOptionTileCTA>
  </AccommodationSameRoomOptionTile>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'offer'
  | 'rooms'
  | 'flightOrigin'
  | 'isLoggedIn'
  | 'isSpoofed'
  | 'subscriber'
  | 'connectRooms'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  offer: state.bedbankOffer!,
  rooms: state.rooms,
  flightOrigin: state.airport,
  isLoggedIn: selectLoggedIn(appState),
  subscriber: getEnquirySubscriber(appState),
  isSpoofed: appState.auth.account.isSpoofed,
  connectRooms: !!state.isMultiRoomConnected,
}), BedbankMultiSameRoomRoomOption)
